// The React glue between the pure gate decision and the browser.
//
// Deliberately THIN. Every decision — held vs anonymous vs error, whether product access
// is granted, how the login URL is built, whether a URL carries a usable exchange code —
// lives in `./gate.ts` and `./dto.ts` as pure functions with unit tests. What is left
// here is effects: call the probe, read `window.location`, navigate, and clean the URL.
//
// That split is why the invariant is checkable. A reviewer verifying
// "held-mode must not become service-mode" reads `productAccess` and `decideGate` — two
// pure functions — and does not have to reason about effect ordering inside a hook.

import * as React from 'react';
import { probeSession, runBridge, type AuthFetchers, type SessionProbe } from './client';
import {
  buildLoginUrl,
  decideGate,
  exchangeCodeFrom,
  type ConnectConfig,
  type GateState,
} from './gate';

export interface UseGateArgs {
  connect: ConnectConfig;
  /** Null when no Connect origin is configured — which means held, not broken. */
  connectBaseUrlOrNull: string | null;
  heldByConfig: boolean;
  /** Where the browser POSTs the bridge. Separate from `connect.authBasePath`. */
  bridgeApiUrl: string;
  /** Injected in tests; real code passes nothing. */
  deps?: AuthFetchers;
}

export interface GateHandle {
  state: GateState;
  /** Re-probe Connect. Used after a bridge and by a manual retry. */
  refresh: () => void;
  /** True while the callback's exchange code is being swapped for a cookie. */
  bridging: boolean;
  /** The bridge's failure detail, shown verbatim. Null when it has not failed. */
  bridgeError: string | null;
}

/**
 * Drive the Path A gate.
 *
 * THE ORDER OF EFFECTS MATTERS:
 *
 * 1. If the current URL is the callback and carries an `exchange_code`, bridge FIRST and
 *    do not probe until it finishes. Probing first would answer "no session" — the
 *    cookie does not exist yet — and `decideGate` would bounce the browser back to
 *    Connect, discarding the exchange code. That is a login loop that looks like a
 *    working redirect.
 * 2. Strip the code from the URL after bridging, with `replaceState`. It is single-use
 *    and IP-bound with a 30s TTL, so leaving it in the address bar invites a reload that
 *    re-POSTs a spent code and fails for a reason the tester cannot see.
 * 3. Only then probe.
 */
export function useGate(args: UseGateArgs): GateHandle {
  const { connect, connectBaseUrlOrNull, heldByConfig, bridgeApiUrl, deps } = args;

  const [probe, setProbe] = React.useState<SessionProbe | null>(null);
  const [bridging, setBridging] = React.useState(false);
  const [bridgeError, setBridgeError] = React.useState<string | null>(null);
  const [nonce, setNonce] = React.useState(0);

  const refresh = React.useCallback(() => {
    setProbe(null);
    setNonce((n) => n + 1);
  }, []);

  React.useEffect(() => {
    // Held deployments ask Connect nothing at all. There is no configured Connect to
    // ask, and a probe against an unconfigured origin produces a network error that
    // renders as "Connect unreachable" on a deployment that is simply not wired yet.
    if (heldByConfig || !connectBaseUrlOrNull) return;

    let cancelled = false;

    void (async () => {
      const href = typeof window === 'undefined' ? '' : window.location.href;
      const code = exchangeCodeFrom(href, connect.callbackPath);

      if (code) {
        setBridging(true);
        setBridgeError(null);
        const outcome = await runBridge(connect, code, bridgeApiUrl, deps);
        if (cancelled) return;
        setBridging(false);
        if (outcome.kind === 'failed') setBridgeError(outcome.detail);
        // Strip the spent code either way — a failed bridge must not leave a code in the
        // address bar for a reload to re-POST.
        stripExchangeCode();
      }

      const result = await probeSession(connect, deps);
      if (!cancelled) setProbe(result);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect, connectBaseUrlOrNull, heldByConfig, bridgeApiUrl, nonce]);

  const loginUrl = React.useMemo(() => {
    if (!connectBaseUrlOrNull || typeof window === 'undefined') return null;
    try {
      return buildLoginUrl(connect, window.location.origin);
    } catch {
      // A login URL that cannot be built is reported by `decideGate` as an error rather
      // than thrown here — a throw in a `useMemo` blanks the page, and a blank page is
      // the one outcome that tells the tester nothing.
      return null;
    }
  }, [connect, connectBaseUrlOrNull]);

  const state = React.useMemo(
    () =>
      decideGate({
        heldByConfig,
        connectBaseUrl: connectBaseUrlOrNull,
        probe,
        loginUrl,
        bridgeFailed: bridgeError,
      }),
    [heldByConfig, connectBaseUrlOrNull, probe, loginUrl, bridgeError],
  );

  // The redirect is an EFFECT, not something computed during render. Navigating from a
  // render body is a side effect in a pure function and double-fires under StrictMode.
  React.useEffect(() => {
    if (state.kind === 'anonymous' && typeof window !== 'undefined') {
      window.location.assign(state.loginUrl);
    }
  }, [state]);

  return { state, refresh, bridging, bridgeError };
}

/** Remove `exchange_code` from the address bar without reloading. */
function stripExchangeCode(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('exchange_code')) return;
    url.searchParams.delete('exchange_code');
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  } catch {
    /* a URL we cannot parse is one we cannot clean; the probe result is unaffected */
  }
}
